import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Task } from '@/lib/types'
import type { CalendarViewMode } from '@/lib/calendarUtils'

export type CurrentView = 'tasks' | 'chat' | 'calendar' | 'groups' | 'ai' | 'profile' | 'settings' | 'faq'

interface UIState {
  isSidebarOpen: boolean
  isTaskCreateModalOpen: boolean
  editingTask: Task | null

  // Category tab
  activeCategoryTab: number | null

  // Calendar
  calendarViewMode: CalendarViewMode
  calendarSelectedDate: string

  // Mobile navigation states
  currentView: CurrentView
  isMobileMenuOpen: boolean

  // Modal states
  isSettingsModalOpen: boolean
  isFAQModalOpen: boolean
  isTermsModalOpen: boolean
  isKeyboardShortcutModalOpen: boolean
  isCommandPaletteOpen: boolean

  // Auxiliary panel (desktop)
  isAuxPanelOpen: boolean
  auxPanelGroupId: string | null  // 공유 작업 선택 시 해당 그룹 ID → AuxPanel에 채팅 표시

  // Selection mode
  isSelectionMode: boolean
  selectedTaskIds: Set<number>

  // Actions
  toggleSelectionMode: () => void
  toggleTaskSelection: (id: number) => void
  selectAllTasks: (ids: number[]) => void
  clearSelection: () => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  openTaskCreateModal: () => void
  openTaskEditModal: (task: Task) => void
  closeTaskCreateModal: () => void

  // Category tab actions
  setActiveCategoryTab: (id: number | null) => void

  // Calendar actions
  setCalendarViewMode: (mode: CalendarViewMode) => void
  setCalendarSelectedDate: (date: string) => void

  // Mobile navigation actions
  setCurrentView: (view: CurrentView) => void
  openMobileMenu: () => void
  closeMobileMenu: () => void
  toggleMobileMenu: () => void

  // Modal actions
  openSettingsModal: () => void
  closeSettingsModal: () => void
  openFAQModal: () => void
  closeFAQModal: () => void
  openTermsModal: () => void
  closeTermsModal: () => void
  openKeyboardShortcutModal: () => void
  closeKeyboardShortcutModal: () => void
  openCommandPalette: () => void
  closeCommandPalette: () => void

  // Auxiliary panel actions
  toggleAuxPanel: () => void
  setAuxPanelGroupId: (groupId: string | null) => void
}

function getTodayDateString(): string {
  // Local timezone — toISOString() returns UTC and is off-by-one near midnight.
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      isSidebarOpen: true,
      isTaskCreateModalOpen: false,
      editingTask: null,

      // Category tab (null = '모두')
      activeCategoryTab: null,

      // Calendar
      calendarViewMode: 'month' as CalendarViewMode,
      calendarSelectedDate: getTodayDateString(),

      // Mobile navigation initial states
      currentView: 'tasks' as CurrentView,
      isMobileMenuOpen: false,

      // Modal initial states
      isSettingsModalOpen: false,
      isFAQModalOpen: false,
      isTermsModalOpen: false,
      isKeyboardShortcutModalOpen: false,
      isCommandPaletteOpen: false,

      // Auxiliary panel
      isAuxPanelOpen: false,
      auxPanelGroupId: null,

      // Selection mode
      isSelectionMode: false,
      selectedTaskIds: new Set<number>(),

      toggleSelectionMode: () => {
        set((state) => ({
          isSelectionMode: !state.isSelectionMode,
          selectedTaskIds: state.isSelectionMode ? new Set<number>() : state.selectedTaskIds,
        }))
      },

      toggleTaskSelection: (id) => {
        set((state) => {
          const next = new Set(state.selectedTaskIds)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return { selectedTaskIds: next }
        })
      },

      selectAllTasks: (ids) => {
        set({ selectedTaskIds: new Set(ids) })
      },

      clearSelection: () => {
        set({ selectedTaskIds: new Set<number>() })
      },

      toggleSidebar: () => {
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen }))
      },

      setSidebarOpen: (open) => {
        set({ isSidebarOpen: open })
      },

      openTaskCreateModal: () => {
        set({ isTaskCreateModalOpen: true, editingTask: null })
      },

      openTaskEditModal: (task) => {
        set({ isTaskCreateModalOpen: true, editingTask: task })
      },

      closeTaskCreateModal: () => {
        set({ isTaskCreateModalOpen: false, editingTask: null })
      },

      // Category tab actions
      setActiveCategoryTab: (id) => {
        set({ activeCategoryTab: id })
      },

      // Calendar actions
      setCalendarViewMode: (mode) => {
        set({ calendarViewMode: mode })
      },
      setCalendarSelectedDate: (date) => {
        set({ calendarSelectedDate: date })
      },

      // Mobile navigation actions
      setCurrentView: (view) => {
        set({ currentView: view })
      },

      openMobileMenu: () => {
        set({ isMobileMenuOpen: true })
      },

      closeMobileMenu: () => {
        set({ isMobileMenuOpen: false })
      },

      toggleMobileMenu: () => {
        set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen }))
      },

      // Modal actions
      openSettingsModal: () => {
        set({ isSettingsModalOpen: true })
      },

      closeSettingsModal: () => {
        set({ isSettingsModalOpen: false })
      },

      openFAQModal: () => {
        set({ isFAQModalOpen: true })
      },

      closeFAQModal: () => {
        set({ isFAQModalOpen: false })
      },

      openTermsModal: () => {
        set({ isTermsModalOpen: true })
      },

      closeTermsModal: () => {
        set({ isTermsModalOpen: false })
      },

      openKeyboardShortcutModal: () => {
        set({ isKeyboardShortcutModalOpen: true })
      },

      closeKeyboardShortcutModal: () => {
        set({ isKeyboardShortcutModalOpen: false })
      },

      openCommandPalette: () => {
        set({ isCommandPaletteOpen: true })
      },

      closeCommandPalette: () => {
        set({ isCommandPaletteOpen: false })
      },

      toggleAuxPanel: () => {
        set((state) => ({ isAuxPanelOpen: !state.isAuxPanelOpen }))
      },
      setAuxPanelGroupId: (groupId) => {
        set({ auxPanelGroupId: groupId })
      },
    }),
    {
      name: 'todo-ui',
      partialize: (state) => ({
        isSidebarOpen: state.isSidebarOpen,
        activeCategoryTab: state.activeCategoryTab,
        calendarViewMode: state.calendarViewMode,
        isAuxPanelOpen: state.isAuxPanelOpen,
      }),
    }
  )
)
